import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { useCart } from "../context/CartContext";

export const SearchBar = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const { addFood } = useCart();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 60); }, [open]);

  useEffect(() => {
    const t = setTimeout(() => {
      const term = q.trim();
      if (term.length < 2) { setResults([]); setSearching(false); return; }
      setSearching(true);
      api.get("/search", { params: { q: term } })
        .then(({ data }) => setResults(data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const close = () => { setOpen(false); setQ(""); setResults([]); };

  const add = async (e, p) => {
    e.stopPropagation();
    if (!p.in_stock || !p.display_price) { toast.error(`${p.name} is sold out today`); return; }
    if (p.base_choices) { toast.error(`${p.name} needs a base — open it on the menu to choose`); return; }
    if (await addFood(p)) toast.success(`${p.name} added to cart`);
  };

  return (
    <>
      <button data-testid="search-button" onClick={() => setOpen(true)} aria-label="Search menu"
        className="p-2.5 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors">
        <Search size={18} aria-hidden="true" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="search-overlay">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />
          <div className="relative max-w-2xl mx-auto mt-16 sm:mt-24 px-4">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-3 px-4 border-b">
                <Search size={18} className="text-neutral-400 shrink-0" />
                <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && close()}
                  placeholder="Search dishes, sweets, groceries…"
                  className="flex-1 h-14 text-[15px] outline-none placeholder:text-neutral-400" />
                <button data-testid="search-close" onClick={close} className="p-2 rounded-full hover:bg-neutral-100">
                  <X size={17} />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
                {searching && <p className="text-sm text-neutral-400 text-center py-6">Searching…</p>}
                {!searching && q.trim().length >= 2 && !results.length && (
                  <p className="text-sm text-neutral-400 text-center py-6" data-testid="search-no-results">
                    No matches for “{q.trim()}” — try another dish or ingredient
                  </p>
                )}
                {!searching && results.map((p) => (
                  <div key={p.id} data-testid={`search-result-${p.id}`}
                    onClick={() => { navigate(`/store/${p.business_slug || "restaurant"}`, { state: { featuredId: p.id } }); close(); }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-0">
                    {p.image ? (
                      <img {...imgProps(p.image, [320], "48px")} alt={p.name} width="48" height="48" className="w-12 h-12 rounded-xl object-cover shrink-0" loading="lazy" />
                    ) : (
                      <span className="w-12 h-12 rounded-xl bg-neutral-100 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate">{p.name}</p>
                      <p className="text-xs text-neutral-400 truncate">{p.business_name}</p>
                    </div>
                    <span className="text-sm font-bold shrink-0">
                      {p.coming_soon ? <span className="text-xs uppercase text-amber-600">Coming soon</span> : p.display_price ? `$${p.display_price.toFixed(2)}` : "—"}
                    </span>
                    {!p.coming_soon && (
                    <button data-testid={`search-add-${p.id}`} onClick={(e) => add(e, p)} aria-label={`Add ${p.name}`}
                      className="shrink-0 w-8 h-8 rounded-full bg-[#E04F33] hover:bg-[#C94228] text-white flex items-center justify-center transition-colors">
                      <Plus size={15} />
                    </button>
                    )}
                  </div>
                ))}
                {!searching && q.trim().length < 2 && (
                  <p className="text-xs text-neutral-400 text-center py-6">Type at least 2 letters — e.g. “butter chicken”, “jalebi”, “atta”</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
